import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { BellRing, Loader2, X } from "lucide-react";
import { enablePush, needsHomeScreen, pushPermission } from "../../lib/push";

const DAY = 86400000;
const key = (where) => `ishukart_push_prompt_${where}`;
const initial = (where) => {
  if (Date.now() < Number(localStorage.getItem(key(where)) || 0)) return null;
  if (pushPermission() === "default") return "ask";
  return needsHomeScreen() ? "homescreen" : null;
};

const COPY = {
  home: ["Get notified when your apps are ready", "We'll ping you when a build finishes, your domain goes live or your credits run low."],
  build: ["Want a ping when this build is done?", "Feel free to leave — we'll notify you."],
  homescreen: ["Get notifications on your iPhone", "Add Ishukart to your Home Screen first — then turn them on there."],
};

// Asks to turn on push notifications: on Home after sign-in, and while a build runs.
export const NotifyPrompt = ({ where = "home", className = "" }) => {
  const [state, setState] = useState(() => initial(where));
  const [busy, setBusy] = useState(false);
  if (!state) return null;
  const hide = () => { localStorage.setItem(key(where), String(Date.now() + 7 * DAY)); setState(null); };
  const turnOn = async () => {
    setBusy(true);
    try {
      const r = await enablePush();
      if (r === "granted") toast.success("Notifications are on");
      else if (r === "denied") toast("Notifications are blocked — allow them for Ishukart in your browser or phone settings");
      setState(null);
    } catch { toast.error("Couldn't turn on notifications on this device"); } finally { setBusy(false); }
  };
  const [title, desc] = COPY[state === "homescreen" ? "homescreen" : where];
  const compact = where === "build";
  return (
    <div data-testid={`notify-prompt-${where}`} role="status"
      className={`relative z-10 flex items-center gap-3 rounded-2xl bg-white ring-1 ring-sky-200 shadow-[0_12px_30px_-18px_rgba(14,165,233,0.6)] fade-up ${compact ? "mx-3 mt-3 p-2.5 pl-3" : "p-4"} ${className}`}>
      <span className={`shrink-0 rounded-xl bg-sky-500 text-white flex items-center justify-center ${compact ? "w-8 h-8" : "w-10 h-10"}`}><BellRing size={compact ? 15 : 18} /></span>
      <div className="min-w-0 flex-1">
        <p className={`font-semibold text-slate-900 ${compact ? "text-[13px]" : "text-sm"}`}>{title}</p>
        <p className="text-xs text-slate-500 truncate sm:whitespace-normal">{desc}</p>
      </div>
      {state === "homescreen" ? (
        <Link to="/get-app#iphone" data-testid={`notify-prompt-${where}-howto`} className="shrink-0 h-8 px-3.5 rounded-full bg-slate-900 text-white text-xs font-semibold flex items-center hover:bg-slate-700">Show me how</Link>
      ) : (
        <button onClick={turnOn} disabled={busy} data-testid={`notify-prompt-${where}-enable`}
          className="shrink-0 h-8 px-3.5 rounded-full bg-slate-900 text-white text-xs font-semibold flex items-center gap-1.5 hover:bg-slate-700 disabled:opacity-60">
          {busy && <Loader2 size={12} className="animate-spin" />} {compact ? "Notify me" : "Turn on"}
        </button>
      )}
      <button onClick={hide} aria-label="Not now" data-testid={`notify-prompt-${where}-dismiss`} className="shrink-0 w-7 h-7 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 flex items-center justify-center"><X size={14} /></button>
    </div>
  );
};

export default NotifyPrompt;
